type GroupColorClasses = {
  bg: string;
  text: string;
  variant: string;
};

const BASE_COLOR_SLUGS = [
  'main-padel',
  'beginners',
  'intermediate',
  'intermediate-plus',
  'advanced',
  'admins',
  'social',
] as const;

const PALETTE_SLUGS = [
  'palette-01',
  'palette-02',
  'palette-03',
  'palette-04',
  'palette-05',
  'palette-06',
  'palette-07',
  'palette-08',
  'palette-09',
  'palette-10',
  'palette-11',
  'palette-12',
  'palette-13',
  'palette-14',
  'palette-15',
  'palette-16',
  'palette-17',
  'palette-18',
  'palette-19',
  'palette-20',
] as const;

type BaseColorSlug = typeof BASE_COLOR_SLUGS[number];
type PaletteColorSlug = typeof PALETTE_SLUGS[number];
type GroupColorSlug = BaseColorSlug | PaletteColorSlug;

const slugToClasses = (slug: string): GroupColorClasses => ({
  bg: `bg-group-${slug}`,
  text: `text-group-${slug}-foreground`,
  variant: slug,
});

const SLUG_CLASS_MAP: Record<string, GroupColorClasses> = [
  ...BASE_COLOR_SLUGS,
  ...PALETTE_SLUGS,
].reduce((acc, slug) => {
  acc[slug] = slugToClasses(slug);
  return acc;
}, {} as Record<string, GroupColorClasses>);

const SECONDARY_COLOR: GroupColorClasses = {
  bg: 'bg-secondary',
  text: 'text-secondary-foreground',
  variant: 'secondary',
};

function normalizeGroupName(name: string): string {
  return name.replace(/\s+/g, ' ').trim().toLowerCase();
}

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

const SPECIAL_GROUP_MAP = new Map<string, GroupColorSlug>();
([
  ['Main Padel Tennis Group', 'main-padel'],
  ['NTNUI Padel - Hovedgruppe / Main Group', 'main-padel'],
  ['Nybegynner / Beginner', 'beginners'],
  ['Middels / Intermediate', 'intermediate'],
  ['Middels Pluss / Intermediate Plus', 'intermediate-plus'],
  ['Avansert / Advanced', 'advanced'],
  ['Administratorer / Admins', 'palette-04'],
  ['Dame / Women', 'palette-03'],
  ['NTNUI Tennis og Padel', 'palette-02'],
  ['Ikke-medlem- må betale drop-in', 'palette-01'],
  ['NTNUI - Team', 'palette-05'],
  ['Sosial / Social', 'social'],
  ['Social / Social', 'social'],
] as Array<[string, GroupColorSlug]>).forEach(([name, slug]) => {
  SPECIAL_GROUP_MAP.set(normalizeGroupName(name), slug);
});

function resolveSlug(groupName: string): GroupColorSlug | 'secondary' {
  if (!groupName) {
    return 'secondary';
  }

  const normalized = normalizeGroupName(groupName);
  if (!normalized) {
    return 'secondary';
  }

  const special = SPECIAL_GROUP_MAP.get(normalized);
  if (special) {
    return special;
  }

  const paletteIndex = hashString(normalized) % PALETTE_SLUGS.length;
  return PALETTE_SLUGS[paletteIndex];
}

export function getGroupColorClasses(groupName: string): GroupColorClasses {
  const slug = resolveSlug(groupName);
  return SLUG_CLASS_MAP[slug] || SECONDARY_COLOR;
}

export const ADMIN_COLORS = {
  bg: 'bg-admin-role',
  text: 'text-admin-role-foreground',
  variant: 'admin',
} as const;

export function getAdminColorClasses() {
  return ADMIN_COLORS;
}

// Utility function to extract the correct language text from bilingual group names
export function getLocalizedGroupName(groupName: string, language: 'no' | 'en'): string {
  if (groupName.includes(' / ')) {
    const [norwegian, english] = groupName.split(' / ');
    return language === 'no' ? norwegian.trim() : english.trim();
  }
  return groupName;
}

export function getLevelGroupName(level?: string): string {
  switch (level) {
    case 'beginner':
      return 'Nybegynner / Beginner';
    case 'intermediate':
      return 'Middels / Intermediate';
    case 'intermediate_plus':
      return 'Middels Pluss / Intermediate Plus';
    case 'advanced':
      return 'Avansert / Advanced';
    default:
      return 'Main Padel Tennis Group';
  }
}
